import { useIsMutating } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Text } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";
import { InputBase } from "@performyard/py-components/src/components/atoms/InputBase";
import { ApproveBudgetData } from "@performyard/service-contracts";

import { Divider } from "../../../../components/Divider";
import { BudgetReviewsTable } from "./components/BudgetReviewsTable";

import { useBulkApproveBudget, useCurrentUser } from "../../../../api/hooks";

import { type ManagerBudgetGroupWithCycleAllotment } from "../SharedComponents/utils";

import ErrorPage from "../../../Error";
import { CycleBudgetSummary } from "../SharedComponents/CycleBudgetSummary";
import { useCycleBudgetOverview } from "../SharedComponents/useCycleBudgetOverview";

type QuickFilterId = "all-budgets" | "approved" | "submitted" | "not-submitted" | "over-budget";

type QuickFilterButtonProps = {
	id: QuickFilterId;
	label: string;
	count: number;
	isSelected: boolean;
	onClick: (id: QuickFilterId) => void;
};

const QuickFilterButton: React.FC<QuickFilterButtonProps> = ({ id, label, count, isSelected, onClick }) => (
	<button
		type="button"
		onClick={() => onClick(id)}
		aria-pressed={isSelected}
		className={`flex h-7 w-full cursor-pointer items-center justify-between gap-2 rounded border-0 px-2 py-0 text-left text-neutral-9 hover:bg-neutral-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-4 ${
			isSelected ? "bg-neutral-1" : "bg-transparent"
		}`}
	>
		<Text size="paragraph-3" weight="regular" color="neutral-9">
			{label}
		</Text>
		<Text size="paragraph-3" weight="regular" color="neutral-7">
			{count}
		</Text>
	</button>
);

const BudgetReviews: React.FC = () => {
	const { cycleId } = useParams<{ cycleId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { data: currentUser } = useCurrentUser();
	const userID = currentUser?.me?.authorization?.payload?.user_id;

	const selectedQuickFilter = (searchParams.get("quickFilter") as QuickFilterId) ?? "all-budgets";
	const setSelectedQuickFilter = (value: QuickFilterId) => {
		setSearchParams(
			(prev) => {
				if (value && value !== "all-budgets") {
					prev.set("quickFilter", value);
				} else {
					prev.delete("quickFilter");
				}
				return prev;
			},
			{ replace: true }
		);
	};
	const searchQuery = searchParams.get("search") ?? "";
	const setSearchQuery = (value: string) => {
		setSearchParams(
			(prev) => {
				if (value) {
					prev.set("search", value);
				} else {
					prev.delete("search");
				}
				return prev;
			},
			{ replace: true }
		);
	};
	const managerIdFilter = searchParams.get("managerId");

	const {
		cycleBudget,
		approvedSpend,
		percentIncrease,
		budgetCount,
		approvedBudgetCount,
		submittedBudgetCount,
		notSubmittedBudgetCount,
		approvedBudgetsPercent,
		notApprovedBudgetsPercent,
		managerGroups,
		approvedBudgets,
		submittedBudgets,
		notSubmittedBudgets,
		overBudgetBudgets,
		overBudgetBudgetCount,
		cycleStatus,
		isLoading,
		isForbidden,
		isNotFound,
		notApprovedSpend,
	} = useCycleBudgetOverview(cycleId);

	const submittedBudgetsForBulkApproval: ApproveBudgetData[] = submittedBudgets.map((submittedBudget) =>
		submittedBudget.results.map((budget) => ({
			employeeId: budget.employeeId.toString(),
			proposedIncreaseAmount: budget?.proposedIncreaseAmount ?? 0,
			proposedIncreasePercent: budget?.proposedIncreasePercent ?? 0,
			proposedSalary: budget?.proposedSalary ?? 0,
		}))
	);
	const { mutate: handleBulkApproveBudgets, isPending: isBulkApproving } = useBulkApproveBudget(
		cycleId || "",
		userID || "",
		submittedBudgetsForBulkApproval
	);
	const isIndividualApproving = useIsMutating({ mutationKey: ["compensation", "approve-budget", cycleId] }) > 0;

	const quickFilters: { id: QuickFilterId; label: string; count: number }[] = [
		{ id: "all-budgets", label: "All Budgets", count: budgetCount },
		{ id: "approved", label: "Approved", count: approvedBudgetCount },
		{ id: "submitted", label: "Submitted", count: submittedBudgetCount },
		{ id: "not-submitted", label: "Not Submitted", count: notSubmittedBudgetCount },
		...(overBudgetBudgets?.length
			? [{ id: "over-budget" as QuickFilterId, label: "Over Budget", count: overBudgetBudgetCount }]
			: []),
	];

	const visibleBudgetGroups = useMemo((): ManagerBudgetGroupWithCycleAllotment[] => {
		let groups: ManagerBudgetGroupWithCycleAllotment[];
		switch (selectedQuickFilter) {
			case "all-budgets":
				groups = managerGroups;
				break;
			case "approved":
				groups = approvedBudgets;
				break;
			case "submitted":
				groups = submittedBudgets;
				break;
			case "not-submitted":
				groups = notSubmittedBudgets;
				break;
			case "over-budget":
				groups = overBudgetBudgets;
				break;
			default:
				groups = [];
		}
		if (managerIdFilter) {
			groups = groups.filter((g) => g.managerId === managerIdFilter || g.overrideManagerId === managerIdFilter);
		}
		const trimmed = searchQuery.trim().toLowerCase();
		if (!trimmed) return groups;
		return groups.filter(
			(g) =>
				g.managerName?.toLowerCase().includes(trimmed) || g.overrideManagerName?.toLowerCase().includes(trimmed)
		);
	}, [
		selectedQuickFilter,
		managerGroups,
		approvedBudgets,
		submittedBudgets,
		notSubmittedBudgets,
		overBudgetBudgets,
		searchQuery,
		managerIdFilter,
	]);

	if (isLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (isNotFound) {
		return <ErrorPage code={404} />;
	}

	if (isForbidden) {
		return <ErrorPage code={403}></ErrorPage>;
	}

	const showApproveAllBudgetsButton = selectedQuickFilter === "submitted" && cycleStatus === "active";

	return (
		<>
			<CycleBudgetSummary
				cycleBudget={cycleBudget}
				approvedSpend={approvedSpend}
				notApprovedSpend={notApprovedSpend}
				percentIncrease={percentIncrease}
				approvedBudgetCount={approvedBudgetCount}
				submittedBudgetCount={submittedBudgetCount}
				notSubmittedBudgetCount={notSubmittedBudgetCount}
				approvedBudgetsPercent={approvedBudgetsPercent}
				notApprovedBudgetsPercent={notApprovedBudgetsPercent}
			/>
			<div className="mt-6 flex w-full items-start gap-6">
				<div className="box-border flex w-[266px] max-w-[266px] shrink-0 flex-col items-start gap-4 rounded-lg border border-solid border-neutral-2 bg-neutral-0 px-[16px] py-4">
					<InputBase
						clearable={true}
						leftIcon={faMagnifyingGlass}
						placeholder="Search by manager..."
						value={searchQuery}
						onChange={setSearchQuery}
						dangerouslySetClassName="w-[234px] min-w-[234px] max-w-[234px]"
					/>
					<div className="flex w-full flex-col gap-2 self-stretch">
						<Text size="paragraph-3" weight="semi-bold">
							Quick Filters
						</Text>
						<div className="flex w-full flex-col gap-[4px] self-stretch">
							{quickFilters
								.filter(({ id }) => id !== "over-budget")
								.map(({ id, label, count }) => (
									<QuickFilterButton
										key={id}
										id={id}
										label={label}
										count={count}
										isSelected={selectedQuickFilter === id}
										onClick={setSelectedQuickFilter}
									/>
								))}
							{quickFilters
								.filter(({ id }) => id === "over-budget")
								.map(({ id, label, count }) => (
									<React.Fragment key={id}>
										<Divider className={"m-0"} />
										<QuickFilterButton
											id={id}
											label={label}
											count={count}
											isSelected={selectedQuickFilter === id}
											onClick={setSelectedQuickFilter}
										/>
									</React.Fragment>
								))}
						</div>
					</div>
				</div>
				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<div className="flex items-center justify-between">
						<Text size="paragraph-2" weight="regular" color="neutral-5">
							Showing: {quickFilters.find((f) => f.id === selectedQuickFilter)?.label ?? ""}
						</Text>
						{showApproveAllBudgetsButton && (
							<ButtonStandard
								size="small"
								variant="solid"
								color="primary"
								dangerouslySetClassName="w-[112px] h-[24px]"
								onClick={() => handleBulkApproveBudgets()}
								disabled={!submittedBudgets?.length || isBulkApproving || isIndividualApproving}
							>
								Approve All Budgets
							</ButtonStandard>
						)}
					</div>
					<BudgetReviewsTable
						key={searchQuery ? "expanded" : "collapsed"}
						cycleStatus={cycleStatus || ""}
						visibleBudgetGroups={visibleBudgetGroups}
						compensationCycleId={cycleId || ""}
						defaultExpanded={!!searchQuery}
					/>
				</div>
			</div>
		</>
	);
};

export default BudgetReviews;
